import { DrawerForm, DrawerSizeEnum } from '@acceso/design-system';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { InputDate } from '@/components/FormFields/InputDate';
import { InputStatusSeenChipSelect } from '@/components/FormFields/InputStatusSeenChipSelect';
import { InputText } from '@/components/FormFields/InputText';
import { RowContainer } from '@/components/FormFields/RowContainer';
import { SelectForm } from '@/components/FormFields/SelectForm';
import { WarningMessage } from '@/components/WarningMessage/WarningMessage';
import { useChannelOptions } from '@/features/broadcasters/hooks/useChannelOptions';
import { DrawerFormDataType, FormTypeEnum } from '@/types';
import { parseAppWallClock } from '@/utils/spain-time';

import { useCNMCDrawerFormState } from '../../hooks/useCNMCDrawerFormState';
import { CNMC, CNMCForm } from '../../types';

export type TCNMCDrawerFormProps = {
  isOpen: boolean;
  data?: DrawerFormDataType<CNMC> | null;
  onClose: () => void;
  onSubmit: (data: CNMCForm) => void;
  isMutating: boolean;
};

export const CNMCDrawerForm = ({
  data,
  isOpen,
  onClose,
  onSubmit,
  isMutating,
}: TCNMCDrawerFormProps) => {
  const { t } = useTranslation();
  const { form } = useCNMCDrawerFormState();

  const handleSubmit = form.handleSubmit(data => {
    onSubmit(data);
  });

  useEffect(() => {
    if (!isOpen) {
      form.reset();
    }
  }, [form.reset, isOpen]);

  useEffect(() => {
    if (!data?.data) return;

    const formData: Partial<CNMCForm> = {
      ...data.data,
      // Stored as Madrid wall clock with no offset (see `CNMC`).
      start_datetime: parseAppWallClock(data.data.start_datetime),
      end_datetime: parseAppWallClock(data.data.end_datetime),
    };

    if (data.type === FormTypeEnum.UPDATE) {
      form.reset(formData);
    }
  }, [data]);
  const channelOptions = useChannelOptions();

  return (
    <DrawerForm
      title={!data?.data ? t('Crear nuevo informe CNMC') : 'Editar informe CNMC'}
      isOpen={isOpen}
      onCancel={onClose}
      isMutating={isMutating}
      onSubmit={handleSubmit}
      size={DrawerSizeEnum.MD}
      labels={{
        submit: t('Guardar'),
        cancel: t('Cancelar'),
      }}
    >
      <InputStatusSeenChipSelect form={form} name="review_state" required />

      <InputText form={form} name="name" label={t('Nombre')} required />

      <InputText form={form} name="description" label={t('Descripción')} required />

      <RowContainer>
        <SelectForm
          form={form}
          name="channel"
          label={t('Emisora')}
          options={channelOptions}
          required
        />

        <InputText form={form} name="provider" label={t('Proveedor')} required />
      </RowContainer>

      <RowContainer>
        <InputDate form={form} name="start_datetime" label={t('Fecha de inicio')} showTimeField />

        <InputDate form={form} name="end_datetime" label={t('Fecha de fin')} showTimeField />
      </RowContainer>

      {/* <InputText
        form={form}
        name="recipients"
        label={t('Emails con aviso de fin de proceso')}
        placeholder="email@email.es,email@email.es"
        tooltip={t(
          'Incluir los emails separador por comas. Cuando termine el proceso el sistema enviará un aviso indicando el estado del proceso.'
        )}
      /> */}

      <WarningMessage
        title={t('Es imprescindible subir los archivos')}
        description={t(
          'Para poder procesar el informe es necesario subir los archivos de vídeo, subtítulos y la escaleta desde la acción de subir archivos'
        )}
      />
    </DrawerForm>
  );
};
